import queryString from 'querystring';
import { addFilterToUrl } from '@prisjakt/product-collection-urls';
import type { ClientMarket } from 'config/types/client-market';

const hasQueryString = (url?: string) =>
  typeof url === 'string' && url.indexOf('?') !== -1;

const removeQueryString = (url?: string) =>
  url && hasQueryString(url) ? url.substring(0, url.indexOf('?')) : url;

const queryStringFromUrl = (url?: string) =>
  url ? url.substr(url.indexOf('?')).substr(1) : '';

const addQueryToUrl = (url?: string, qs?: string, routeSection?: string) => {
  const baseUrl = hasQueryString(url) ? removeQueryString(url) : url;

  if (!baseUrl) return '';

  const routeSectionFragment = routeSection ? `/${routeSection}` : '';
  const qsFragment = qs ? `?${qs}` : '';

  return `${baseUrl}${routeSectionFragment}${qsFragment}`;
};

// eslint-disable-next-line @typescript-eslint/ban-types
const getQueryString = (url?: string, query?: {}) => {
  // Combine parameters from URL with query object
  const combinedQuery = Object.assign(
    queryString.decode(queryStringFromUrl(url)),
    query
  );
  // Sort object keys to ensure constant parameter order
  const sortedQuery = Object.keys(combinedQuery)
    .sort()
    .reduce((q, key) => ({ ...q, [key]: combinedQuery[key] }), {});
  return queryString.encode(sortedQuery);
};

export const withQueryString = (
  url?: string,
  query?: Record<string, any>,
  routeSection?: string
) => addQueryToUrl(url, getQueryString(url, query), routeSection);

function productSectionToClassicIdParam(section = 'main') {
  switch (section) {
    case 'properties':
      return 'e';
    case 'reviews':
      return 'o';
    case 'statistics':
      return 'pu';
    default:
      return 'p';
  }
}

const linkTo = (
  useClassicUrls: ClientMarket['useClassicUrls'],
  useHashForProductTabs: ClientMarket['useHashForProductTabs'],
  classicRoutes: ClientMarket['classicRoutes'],
  routes: ClientMarket['routes'],
  fallbackRedirects: ClientMarket['fallbackRedirects'],
  type: string | undefined,
  item: Record<string, any> | undefined = undefined,
  query: Record<string, any> = {},
  routeSection?: string
): string => {
  // Support both graphql schema and api schema
  const semanticId = item && (item.pathName || item.semantic_id || item.path);
  const externalUri = item && (item.externalUri || item.redirect_uri);

  function linkToProduct(section?: string) {
    const classicIdParam = useHashForProductTabs
      ? 'p'
      : productSectionToClassicIdParam(section);

    const path = useClassicUrls
      ? // If useClassicUrls is true then classicRoutes should exist
        withQueryString(classicRoutes!.product, {
          ...query,
          [classicIdParam]: item?.id,
        })
      : `${withQueryString(semanticId, query)}${
          !useHashForProductTabs && section ? `/${section}` : ''
        }`;

    return useHashForProductTabs && section ? `${path}#${section}` : path;
  }

  switch (type) {
    case 'raw': {
      return externalUri;
    }
    case 'product':
    case 'raw_sorted': {
      return linkToProduct();
    }
    case 'productProperties': {
      return linkToProduct('properties');
    }
    case 'productReviews': {
      return linkToProduct('reviews');
    }
    case 'productPriceHistory': {
      return linkToProduct('statistics');
    }
    case 'productAlert': {
      return useClassicUrls
        ? withQueryString(
            classicRoutes!.product + routes.productAlert.relativePath,
            { ...query, p: item?.id }
          )
        : withQueryString(semanticId + routes.productAlert.relativePath, query);
    }
    case 'productCompare': {
      const { category, products } = item!;
      const categoryId = category && category.id;

      const productIds = Array.isArray(products)
        ? products.map(p => p.id).join(',')
        : '';

      return withQueryString(
        routes.productCompare.path.replace(':productIds', productIds),
        categoryId ? { categoryId } : {}
      );
    }
    case 'brand': {
      return useClassicUrls
        ? withQueryString(classicRoutes!.brand, { ...query, t: item?.id })
        : withQueryString(semanticId, query);
    }

    case 'store': {
      return useClassicUrls
        ? withQueryString(
            classicRoutes!.store_info,
            { ...query, f: item?.id },
            routeSection
          )
        : withQueryString(semanticId, query, routeSection);
    }

    case 'brandCategory':
    case 'brand_category':
    case 'brand_virtual_category':
      if (item?.product_collection || item?.productCollection) {
        // eslint-disable-next-line no-param-reassign
        query.brand = item?.brandId;
      } else if (useClassicUrls) {
        // eslint-disable-next-line no-param-reassign
        query.t = item?.brandId;
      } else {
        // eslint-disable-next-line no-param-reassign
        query.brand = item?.brandId;
      }
    case 'virtual_category': // eslint-disable-line no-fallthrough
    case 'virtualCategory':
    case 'category': {
      if (
        item?.type === 'productCollection' ||
        item?.product_collection ||
        item?.productCollection
      ) {
        return withQueryString(
          item.product_collection || item.productCollection || item.path,
          query
        );
      }

      if (useClassicUrls) {
        return withQueryString(classicRoutes!.category, {
          ...query,
          k: item?.id,
        });
      }

      if (
        semanticId == null &&
        fallbackRedirects &&
        fallbackRedirects[item?.id]
      ) {
        return fallbackRedirects[item?.id];
      }

      return withQueryString(semanticId, query);
    }
    case 'shopOfTheYear': {
      return routes.shopOfTheYear.path;
    }
    case 'shopOfTheYearCategory': {
      return routes.shopOfTheYearCategory.path
        .replace(':year', item?.year)
        .replace(':id', item?.id);
    }
    case 'shopOfTheYearWinners': {
      return `${routes.shopOfTheYearWinners.path.replace(
        ':section',
        item?.year
      )}#winners`;
    }
    case 'productCollection': {
      const { filters = [] } = item!;

      return filters.reduce(addFilterToUrl, semanticId);
    }
    case 'productCollectionSitemap': {
      const { baseUrl } = item!;
      return removeQueryString(baseUrl.replace('/c/', '/cm/'))!;
    }
    case 'editorialContentListing': {
      return routes.editorialContentListing.path;
    }
    case 'user': {
      return `/user/${item?.id}`;
    }
    case 'list': {
      return withQueryString(semanticId, query);
    }
    case 'categoryIndex': {
      return routes.categoryIndex.path;
    }
    case 'expertTest': {
      return withQueryString(semanticId, query);
    }
    case 'expertToplist': {
      return withQueryString(semanticId, query);
    }
    case 'expertCategory': {
      return withQueryString(semanticId, query);
    }
    case 'expertProfile': {
      return withQueryString(semanticId, query);
    }
    case 'expertContent': {
      return withQueryString(semanticId, query);
    }
    case 'expertMedia': {
      return withQueryString(semanticId, query);
    }
    case 'expertArticle': {
      return withQueryString(semanticId, query);
    }
    case 'experts': {
      return withQueryString(routes.expertLandingPage.path, query);
    }
    case 'changelog': {
      return routes.changelogLandingPage?.path;
    }
    case 'changelogArticle': {
      return `/${item?.path}`;
    }
    case 'changelogLatest': {
      return routes.changelogLatest?.path;
    }
    case 'keywordListing': {
      const slug = item?.url && item.url.slug ? item.url.slug : item?.slug;
      return `/kw/${slug || item?.id}`;
    }
    case 'search': {
      return `/search?search=${item?.query}`;
    }

    default:
      return '';
  }
};

export { linkTo, addQueryToUrl };
